import React, { PropsWithChildren, useState } from 'react'
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { STAKE_COMMISSION } from '@plug-core/constants/business'
import { ICToken } from '@plug-core/interfaces/common'
import { WaterNeuronInfo } from '@plug-core/interfaces/staking'
import { WaterNeuron } from '@plug-core/clients/WaterNeuron'
import { useStaking } from '@plug-core/hooks/useStaking'
import { TokenCard } from '@modules/Swap/ExchangeView'
import { AnimationPlayer } from '@components/AnimationPlayer'
import Button from '@components/Button'
import BottomSheet from '@components/BottomSheet'
import Prompt from '@components/Prompt'
import successAnimation from '@assets/lottie/success.json'
import { StakeButton } from './StakeButton'
import { UnstakeButton } from './UnstakeButton'
import { StakeReview } from './StakeReview'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { HiInformationCircle } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

const StakeListItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListItem
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      my: 1,
    }}
  >
    <Typography variant="body2">{attribute}</Typography>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  </ListItem>
)

export const WaterNeuronStaking = ({
  icpToken,
  nicpToken,
  wnInfo,
  waterNeuron,
}: {
  icpToken: ICToken
  nicpToken: ICToken
  wnInfo: WaterNeuronInfo
  waterNeuron: WaterNeuron
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const onInfoClick = (event: any) => {
    event.stopPropagation()

    setPrompt({
      title: t(`stake.titleAbout`),
      description: t(`stake.about`),
    })
  }

  const {
    stakeMode,
    setStakeMode,
    token,
    amount,
    setAmount,
    numericAmount,
    exchangeRate,
    setReviewData,
    isLoading,
    commission,
    success,
    setSuccess,
    reviewData,
    onSubmit,
    nicpReceiveAmount,
    wtnReward,
  } = useStaking({ icpToken, nicpToken, wnInfo, waterNeuron })

  return (
    <Stack pb={1} pt={2}>
      <Titlebar
        title={t('stake.title')}
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        leftItem={
          <IconButton sx={{ width: 40, marginRight: 1 }} color="primary" onClick={onInfoClick}>
            <HiInformationCircle size={40} />
          </IconButton>
        }
      />
      <Box borderBottom={1} borderColor="divider" mb={2} mt={0.75} mx={2.5}>
        <Tabs
          value={stakeMode === 'stake' ? 0 : 1}
          onChange={(_e, index) => setStakeMode(index === 0 ? 'stake' : 'unstake')}
        >
          <Tab label={t('stake.stakeIcp')} sx={{ width: '50%' }} />
          <Tab label={t('stake.unstakeNicp')} sx={{ width: '50%' }} />
        </Tabs>
      </Box>

      <Stack mx={2}>
        <TokenCard
          token={{ ...token, id: token.address }}
          amount={amount}
          setAmount={setAmount}
          mode="stake"
          error={undefined}
          showBalance
          disableTokenPicker
        />
        <Stack mt={1.25}>
          <List component={Paper} disablePadding>
            {stakeMode === 'stake' && (
              <StakeListItem attribute={t('stake.receiveAmount')}>
                {nicpReceiveAmount}
              </StakeListItem>
            )}
            <Divider sx={{ borderColor: 'background.default' }} />

            <StakeListItem attribute={t('stake.exchangeRate')}>{exchangeRate}</StakeListItem>

            {stakeMode === 'stake' && (
              <>
                <Divider sx={{ borderColor: 'background.default' }} />
                <StakeListItem attribute={t('stake.futureReward')}>{wtnReward}</StakeListItem>
              </>
            )}
          </List>
        </Stack>
      </Stack>

      <Stack mt={1.25} mx={2}>
        {stakeMode === 'stake' && (
          <StakeButton
            review={setReviewData}
            waterNeuron={waterNeuron}
            isLoading={isLoading}
            amount={numericAmount}
            commission={commission}
            wnInfo={wnInfo}
            icpToken={icpToken}
            nicpToken={nicpToken}
          />
        )}

        {stakeMode === 'unstake' && (
          <UnstakeButton
            review={setReviewData}
            waterNeuron={waterNeuron}
            isLoading={isLoading}
            amount={numericAmount}
            commission={commission}
            wnInfo={wnInfo}
            icpToken={icpToken}
            nicpToken={nicpToken}
          />
        )}
      </Stack>

      <BottomSheet visible={!!success}>
        <Stack>
          <Typography variant="h6" textAlign="center" mt={2}>
            {t('stake.success')}
          </Typography>
          <AnimationPlayer animationData={successAnimation} />
        </Stack>
      </BottomSheet>

      <BottomSheet
        visible={!!reviewData}
        onBackdropClick={() => !isLoading && setReviewData(undefined)}
      >
        <StakeReview
          data={reviewData!}
          amount={amount}
          fee={STAKE_COMMISSION}
          mode={stakeMode}
          isLoading={isLoading}
          icpToken={icpToken}
          nicpToken={nicpToken}
          submit={onSubmit}
          close={() => setReviewData(undefined)}
        />
      </BottomSheet>

      <Prompt show={success === false} close={() => setSuccess(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {t('stake.error.title')}
            </Typography>
            <Typography textAlign="center" mt={1} mb={2}>
              {t('stake.error.description')}
            </Typography>
          </Stack>

          <Button onClick={() => setSuccess(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography textAlign="center" mt={1} mb={2} color="text.light">
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>
    </Stack>
  )
}
